import { GetStaticProps } from "next"
import { useState } from "react"
import { Container, Box, Grid, TextField, Paper } from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import { mdiSendOutline, mdiMapMarker, mdiPhoneForward, mdiSendCheckOutline } from "@mdi/js"
import ReCAPTCHA from "react-google-recaptcha"
import { useSnackbar } from "notistack"

import SEO from "components/SEO"
import { Phone, Email } from "components/i18n/Contact"
import Button from "components/Buttons/Button"
import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"

import useTranslation from "hooks/useTranslation"
import useInputValue from "hooks/useInputValue"

import { getServerTranslations } from "utils/i18n/getServerTranslations"
import { helpCenterBreadCrumb } from "./index"

const useStyles = makeStyles()((theme) => ({
	root: {
		padding: theme.spacing(3, 2),
		color: "black",
	},
	map: {
		boxShadow: "0px -7px 5px 0px rgba(0,0,0,0.10)",
	},
}))

const subjects = ["delivery", "product", "assembly", "payment"]

const sendContactForm = async (
	firstName: string,
	lastName: string,
	email: string,
	subject: string,
	message: string,
) => {
	const { origin } = window.location
	const response = await fetch(`${origin}/api/email/sendContactForm`, {
		method: "POST",
		body: JSON.stringify({
			name: `${firstName} ${lastName}`,
			email,
			subject,
			message,
		}),
	})
	return response
}

const ContactPage = () => {
	const { classes } = useStyles()
	const subject = useInputValue(subjects[0])
	const firstName = useInputValue()
	const lastName = useInputValue()
	const email = useInputValue()
	const message = useInputValue()
	const { enqueueSnackbar } = useSnackbar()

	const [recaptchaValue, setRecaptchaValue] = useState(false)

	const { t } = useTranslation("help")
	const [isSent, setIsSent] = useState(false)

	const breadCrumb: BreadCrumbAncestor = {
		label: t("contact"),
		href: "/help/contact",
	}

	const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		if (recaptchaValue === false) {
			enqueueSnackbar("Bitte bestätigen sie das Captcha.", { variant: "error" })
			return
		}

		setIsSent(false)

		const isValid = [email.value, firstName.value, lastName.value, message.value].reduce(
			(isValid, value) => isValid && value.length > 0,
			true,
		)

		if (!isValid) return

		await sendContactForm(
			firstName.value,
			lastName.value,
			email.value,
			subject.value,
			message.value,
		)

		setIsSent(true)
	}

	return (
		<>
			<SEO pageType="contact" asPath="/contact" />
			<Box style={{ paddingBottom: "20px" }}>
				<BreadCrumbs
					breadCrumbsPaths={[
						{ ...helpCenterBreadCrumb, label: t(helpCenterBreadCrumb.label) },
					]}
					currentPath={breadCrumb}
				/>
			</Box>
			<Container>
				<Box my={5} py={5}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={5}>
							<h1 style={{ margin: "0" }}>{t("contactSafehero")}</h1>
							<p style={{ marginTop: "0" }}>{t("contactSafeheroText")}</p>
							<Box my={5} style={{ maxWidth: "500px" }}>
								<p>{t("companyDescription")}</p>
							</Box>

							<Grid container>
								<Grid item xs={6}>
									<Box mb={1} style={{ display: "flex" }}>
										<Icon path={mdiMapMarker} size={1} color="#7abfac" />{" "}
										{t("address")}
									</Box>
									<div>Im Moos 19</div>
									<div>6067 Absam AT</div>
								</Grid>
								<Grid item xs={6}>
									<Box mb={1} style={{ display: "flex" }}>
										<Icon path={mdiPhoneForward} size={1} color="#7abfac" />{" "}
										{t("phone")}
									</Box>
									<div>
										<Phone />
									</div>
									<div>
										<Email />
									</div>
								</Grid>
							</Grid>
						</Grid>
						{isSent ? (
							<Paper className={classes.root}>
								<div style={{ textAlign: "center" }}>
									<Icon path={mdiSendCheckOutline} size={2} color="#7abfac" />
								</div>
								<div>{t("contactFormSentText")}</div>
							</Paper>
						) : (
							<Grid item xs={12} md={7}>
								<form onSubmit={submitForm}>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<TextField
												id="outlined-select-subject-native"
												select
												label={t("subject")}
												{...subject}
												name="subject"
												SelectProps={{
													native: true,
												}}
												margin="dense"
												variant="outlined"
												fullWidth
											>
												{subjects.map((subject) => (
													<option key={subject} value={subject}>
														{t(subject)}
													</option>
												))}
											</TextField>
											<TextField
												required
												id="outlined-required"
												label={t("firstName")}
												name="firstName"
												margin="dense"
												variant="outlined"
												{...firstName}
												fullWidth
											/>
											<TextField
												required
												id="outlined-required"
												name="lastName"
												label={t("lastName")}
												margin="dense"
												variant="outlined"
												{...lastName}
												fullWidth
											/>
											<TextField
												required
												id="outlined-required"
												label={t("email")}
												name="email"
												type="email"
												margin="dense"
												variant="outlined"
												{...email}
												fullWidth
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<TextField
												required
												id="outlined-required"
												label={t("yourMessage")}
												name="message"
												multiline
												rows="6"
												margin="dense"
												variant="outlined"
												{...message}
												fullWidth
											/>
											<p className="small" style={{ color: "grey" }}>
												{t("contactFormDisclaimer")}
											</p>
											<Box my={2}>
												{/* TODO: Move to .env file */}
												<ReCAPTCHA
													size="normal"
													sitekey="6LcBgkYqAAAAAC3iLE4e90A2qaRNDgl7pzu1o5h-"
													onChange={setRecaptchaValue}
												/>
											</Box>
											<Button color="primary" type="submit">
												<Box mr={1}>{t("send")}</Box>
												<Icon
													path={mdiSendOutline}
													size={0.8}
													color="#f7fafa"
												/>
											</Button>
										</Grid>
									</Grid>
								</form>
							</Grid>
						)}
					</Grid>
				</Box>
			</Container>
			<div className={classes.map}>
				<Box className="mapouter">
					<div className="gmap_canvas">
						<iframe
							title="Google Maps Secureo"
							width="100%"
							height="500"
							id="gmap_canvas"
							src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2705.9228109358437!2d11.50565193389063!3d47.29630801252558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479d6f7c737fbdcd%3A0x1e03ee85b9232ca9!2sIm%20Moos%2019%2C%206067%20Absam!5e0!3m2!1sen!2sat!4v1674488283393!5m2!1sen!2sat"
							frameBorder="0"
							scrolling="yes"
						/>
					</div>
				</Box>
			</div>
		</>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const props = {
		...(await getServerTranslations(context.locale, [
			"common",
			"countries",
			"languages",
			"help",
		])),
	}

	return {
		props,
	}
}

export default ContactPage
